import supabase from '@/lib/supabase';

interface LearnTrackData {
  user_id: string;
  quest_id: string;
  quest_name?: string;
  learn_duration_seconds: number;
}

export interface CalendarDay {
  date: string; // 格式 YYYY-MM-DD
  hasLearned: boolean;
}

interface QuestDuration {
  quest_id: string;
  quest_name: string | null;
  duration_seconds: number;
}

interface DailyTotal {
  date: string;
  total_duration: number;
}

interface QuestTotal {
  quest_id: string;
  quest_name: string | null;
  total_duration: number;
}

class UserTrackService {
  /**
   * 跟踪用户学习时长
   * @param data 学习数据，包含用户ID、任务ID和学习时长
   */
  static async trackLearnDuration(data: LearnTrackData): Promise<void> {
    try {
      const now = new Date();
      const date = now.toISOString().split('T')[0]; // 格式: YYYY-MM-DD
      const date_hour = now.getHours(); // 0-23 小时

      // 先检查记录是否存在
      const { data: existingRecord, error: fetchError } = await supabase
        .from('user_learn_track')
        .select('learn_duration_seconds')
        .match({
          user_id: data.user_id,
          date: date,
          date_hour: date_hour,
          quest_id: data.quest_id
        })
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 是"没有找到记录"的错误
        console.error('Error fetching existing record:', fetchError);
        return;
      }

      // 计算新的累积学习时长
      const currentDuration = existingRecord?.learn_duration_seconds || 0;
      const newDuration = currentDuration + data.learn_duration_seconds;

      // 使用 upsert 操作 - 如果记录存在则更新，不存在则插入
      const { error } = await supabase
        .from('user_learn_track')
        .upsert(
          {
            user_id: data.user_id,
            date: date,
            date_hour: date_hour,
            quest_id: data.quest_id,
            quest_name: data.quest_name || null,
            learn_duration_seconds: newDuration, // 累积学习时长
            updated_at: new Date().toISOString()
          },
          { 
            onConflict: 'user_id,date,date_hour,quest_id', // 更新冲突时的条件
            ignoreDuplicates: false
          }
        );

      if (error) {
        console.error('Error tracking learn duration:', error);
      }
    } catch (err) {
      console.error('Failed to track learn duration:', err);
    }
  }

  /**
   * 获取用户当月的学习日历数据
   * 当天学习时长大于2分钟的为true，否则为false
   * @param userId 用户ID
   * @param month 月份 (1-12)，默认为当前月
   * @param year 年份，默认为当前年
   * @returns 日历数据数组
   */
  static async getLearnCalendar(userId: string, month?: number, year?: number): Promise<CalendarDay[]> {
    try {
      // 设置当前查询的月份和年份
      const now = new Date();
      const targetYear = year || now.getFullYear();
      const targetMonth = month || now.getMonth() + 1; // JS 月份是 0-11，我们使用 1-12

      // 获取月份的第一天和最后一天
      const firstDay = new Date(targetYear, targetMonth - 1, 1);
      const lastDay = new Date(targetYear, targetMonth, 0);

      const firstDayStr = firstDay.toISOString().split('T')[0]; // YYYY-MM-DD
      const lastDayStr = lastDay.toISOString().split('T')[0]; // YYYY-MM-DD

      // 使用SQL聚合函数直接在数据库中计算每天的总学习时长
      // 通过日期分组，计算每天的学习总时长
      const { data, error } = await supabase
        .rpc('get_user_daily_learn_totals', {
          user_id_param: userId,
          start_date_param: firstDayStr,
          end_date_param: lastDayStr,
          user_timezone_param: Intl.DateTimeFormat().resolvedOptions().timeZone
        });

      if (error) {
        console.error('Error fetching learn calendar data:', error);
      }

      // 数据是直接从DB聚合的，格式为：{date: string, total_duration: number}[]
      const dailyTotals: Record<string, number> = {};
      data?.forEach((item: DailyTotal) => {
        dailyTotals[item.date] = item.total_duration;
      });

      // 构建整个月的日历数据
      const calendarData: CalendarDay[] = [];
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(targetYear, targetMonth - 1, day);
        const dateStr = date.toISOString().split('T')[0];
        
        // 大于2分钟(120秒)的学习时间标记为true
        const dailyDuration = dailyTotals[dateStr] || 0;
        calendarData.push({
          date: dateStr,
          hasLearned: dailyDuration > 0
        });
      }

      return calendarData;
    } catch (err) {
      console.error('Failed to get learn calendar:', err);
      return [];
    }
  }

  /**
   * 获取用户在每个任务上的累积学习时长
   * @param userId 用户ID
   * @returns 每个任务的累积学习时长
   */
  static async getLearnDuration(userId: string): Promise<QuestDuration[]> {
    try {
      // 使用SQL聚合函数直接在数据库中计算每个quest的总学习时长
      const { data, error } = await supabase
        .rpc('get_quest_learn_totals', {
          user_id_param: userId
        });

      if (error) {
        console.error('Error fetching learn duration data:', error);
      }

      // 数据已经从数据库聚合好了，格式为：{quest_id: string, quest_name: string, total_duration: number}[]
      return data.map((item: QuestTotal) => ({
        quest_id: item.quest_id,
        quest_name: item.quest_name,
        duration_seconds: item.total_duration
      }));
    } catch (err) {
      console.error('Failed to get learn durations:', err);
      return [];
    }
  }
}

export default UserTrackService; 